import { xor } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsTemplateDisableUnsubscribeUrlValidationFeatureFlagOn } from 'core/userContext';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import {
  useValidateContentQuery,
  ValidateContentQueryVariables,
} from 'modules/campaigns/lib/hooks/useValidateContentQuery';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';

import { GqlInvalidContentReason } from '../../common';
import { dedupeArray } from '../../common/utils';
import { useQueryParamContext } from '../QueryParamContext';
import { useTemplateQueryContext } from '../TemplateQueryContext';
import { getAllowedValidationFailures } from './ErrorValidationContextProvider.utils';

export const useLocaleErrorCollector = (
  currentLocaleTag: string,
  defaultLocaleTag: string,
  templateLocaleTags: string[],
) => {
  const dispatch = useDispatch();
  const { messageTypeId } = useTemplateQueryContext();

  const { templateId } = useQueryParamContext();
  const { isLoading: isLoadingLocales } = useLocalesQueryContext();

  const isTemplateDisableUnsubscribeUrlValidationFeatureFlagOn = useSelector(
    selectIsTemplateDisableUnsubscribeUrlValidationFeatureFlagOn,
  );

  const projectHasNoLocales = defaultLocaleTag === '';

  const [localeBeingValidated, setLocaleBeingValidated] = useState<null | string>(null);

  const [validatedLocaleTags, setValidatedLocaleTags] = useState<string[]>([]);
  const [localeTagsToValidate, setLocaleTagsToValidate] = useState<string[]>([]);
  const [allContentErrors, setAllContentErrors] = useState<string[]>([]);
  const [localeErrors, setLocaleErrors] = useState<GqlInvalidContentReason[]>([]);

  const isFinishedValidating = !xor(templateLocaleTags, validatedLocaleTags).length;

  const generateValidateVariables = useCallback(
    (inputLocale: string | null): ValidateContentQueryVariables => {
      const localeVariable =
        !inputLocale && projectHasNoLocales
          ? undefined
          : { locale: { name: !inputLocale ? defaultLocaleTag : inputLocale } };
      return {
        templateId: templateId || -1,
        messageTypeId,
        ...localeVariable,
      };
    },
    [projectHasNoLocales, templateId, messageTypeId, defaultLocaleTag],
  );

  const primaryValidationVariables = generateValidateVariables(currentLocaleTag);

  const {
    validationFailures: primaryValidationFailures,
    locale: primaryLocale,
    isLoading: isLoadingPrimaryValidations,
    refetch: refetchPrimary,
  } = useValidateContentQuery({
    errorPolicy: 'all',
    variables: primaryValidationVariables,
    skip: isLoadingLocales || messageTypeId === -1 || templateId === -1,
    onError(error) {
      dispatch(
        addNegativeToastWithErrorId(
          `Error validating content. Please try again later.`,
          'useLocaleErrorCollector',
          error,
        ),
      );
    },
  });

  const validatedPrimaryLocaleTag = primaryLocale?.name;
  const primaryFailureCount = primaryValidationFailures.length || 0;

  const nonPrimaryValidationVariables = generateValidateVariables(localeBeingValidated);

  const {
    validationFailures: nonPrimaryValidationFailures,
    locale: nonPrimaryLocale,
    isLoading: isLoadingNonPrimaryValidations,
    refetch: refetchNonPrimary,
  } = useValidateContentQuery({
    errorPolicy: 'all',
    variables: nonPrimaryValidationVariables,
    skip: !localeBeingValidated || isLoadingLocales || messageTypeId === -1 || templateId === -1,
    onError(error) {
      dispatch(
        addNegativeToastWithErrorId(
          `Error validating content. Please try again later.`,
          'useLocaleErrorCollector',
          error,
        ),
      );
    },
  });
  const validatedNonPrimaryLocaleTag = nonPrimaryLocale?.name;
  const nonPrimaryFailureCount = nonPrimaryValidationFailures.length || 0;

  const primitivePrimaryValidationFailures = JSON.stringify(primaryValidationFailures);
  const primitiveNonPrimaryValidationFailures = JSON.stringify(nonPrimaryValidationFailures);
  const primitiveTemplateLocaleTags = JSON.stringify(templateLocaleTags);
  const primitiveValidatedLocaleTags = JSON.stringify(validatedLocaleTags);
  const primitiveLocaleTagsToValidate = JSON.stringify(localeTagsToValidate);

  const storeValidationChecks = useCallback(
    (inputLocaleName = '', validationCheckCount = 0) => {
      const aryTemplateLocales = JSON.parse(primitiveTemplateLocaleTags);

      if (projectHasNoLocales || aryTemplateLocales.includes(inputLocaleName)) {
        setValidatedLocaleTags((vl) => dedupeArray([...vl, inputLocaleName]));
        setAllContentErrors((ace) => {
          if (!ace.includes(inputLocaleName) && validationCheckCount) {
            return [...ace, inputLocaleName];
          } else if (ace.includes(inputLocaleName) && !validationCheckCount) {
            return ace.filter((ce) => ce !== inputLocaleName);
          }
          return ace;
        });
      }
      setLocaleTagsToValidate((ltv) => ltv.filter((lt) => lt !== inputLocaleName));
    },
    [
      primitiveTemplateLocaleTags,
      primitiveNonPrimaryValidationFailures,
      primitivePrimaryValidationFailures,
      projectHasNoLocales,
      validatedPrimaryLocaleTag,
      validatedNonPrimaryLocaleTag,
    ],
  );

  useEffect(() => {
    const parsedPrimaryValidationFailures = JSON.parse(primitivePrimaryValidationFailures);
    const validationFailures = getAllowedValidationFailures(
      parsedPrimaryValidationFailures,
      isTemplateDisableUnsubscribeUrlValidationFeatureFlagOn,
    );
    setLocaleErrors(validationFailures);
  }, [primitivePrimaryValidationFailures, isTemplateDisableUnsubscribeUrlValidationFeatureFlagOn]);

  useEffect(() => {
    storeValidationChecks(validatedNonPrimaryLocaleTag, nonPrimaryFailureCount);
  }, [validatedNonPrimaryLocaleTag, nonPrimaryFailureCount, storeValidationChecks]);

  useEffect(() => {
    storeValidationChecks(validatedPrimaryLocaleTag, primaryFailureCount);
  }, [validatedPrimaryLocaleTag, primaryFailureCount, storeValidationChecks]);

  // useEffect
  useEffect(() => {
    // when the list of template locales or template id changes validate the new locales added
    const parsedPrimitiveLocaleTags = JSON.parse(primitiveTemplateLocaleTags);
    const parsedValidatedLocaleTags = JSON.parse(primitiveValidatedLocaleTags);

    // grab locales to validate and remove the current locale as it will be handled by the primary
    const filteredPrimitiveLocalTags = parsedPrimitiveLocaleTags.filter(
      (i: string) => i !== currentLocaleTag,
    );

    // remove any of the locales that do not exist in our active locales, incase we have any stray ones
    const filteredValidatedLocalTags = parsedValidatedLocaleTags.filter(
      (i: string) => parsedPrimitiveLocaleTags.indexOf(i) !== -1,
    );
    setValidatedLocaleTags(filteredValidatedLocalTags);
    setLocaleTagsToValidate(filteredPrimitiveLocalTags);
  }, [primitiveTemplateLocaleTags]);

  useEffect(() => {
    const aryTemplateLocales: string[] = JSON.parse(primitiveLocaleTagsToValidate);
    const aryValidatedLocales: string[] = JSON.parse(primitiveValidatedLocaleTags);
    // get the next locale to validate (get the first result that has not been validated yet)
    const localeToValidate = aryTemplateLocales.find(
      (templateLocale) => !aryValidatedLocales.includes(templateLocale),
    );
    if (localeToValidate) {
      if (localeToValidate === localeBeingValidated) {
        refetchNonPrimary(generateValidateVariables(localeToValidate));
      } else {
        setLocaleBeingValidated(localeToValidate);
      }
    }
  }, [primitiveValidatedLocaleTags, primitiveLocaleTagsToValidate]);

  const clearLocaleValidation = (localeTag: string) => {
    setLocaleErrors([]);
    setAllContentErrors((ace) => ace.filter((a) => a !== localeTag));
    setValidatedLocaleTags((vl) => vl.filter((v) => v !== localeTag));
  };
  const revalidateLocales = useCallback(() => {
    // clear the error states

    setLocaleErrors([]);
    setAllContentErrors([]);

    // clear our locale tracking and queue
    setLocaleTagsToValidate([]);
    setValidatedLocaleTags([]);

    // start the locales being validated
    const parsedPrimitiveLocaleTags = JSON.parse(primitiveTemplateLocaleTags);
    const filteredPrimitiveLocalTags = parsedPrimitiveLocaleTags.filter(
      (i: string) => i !== currentLocaleTag,
    );
    setLocaleTagsToValidate(filteredPrimitiveLocalTags); // this forces a refresh

    refetchPrimary(generateValidateVariables(currentLocaleTag));
  }, [primitiveTemplateLocaleTags, currentLocaleTag, generateValidateVariables, refetchPrimary]);

  const revalidateLocale = (localeTag: string) => {
    if (localeTag === '' || localeTag === currentLocaleTag) {
      refetchPrimary(generateValidateVariables(localeTag)); // force revalidation on primary locale
    } else {
      refetchNonPrimary(generateValidateVariables(localeTag)); // force revalidation and ignore the queue for nonprimary.
    }
  };

  return {
    validationFailures: localeErrors,
    isLoadingValidations: isLoadingPrimaryValidations || isLoadingNonPrimaryValidations,
    allContentErrors,
    isFinishedValidating,
    clearLocaleValidation,
    revalidateLocales,
    revalidateLocale,
  };
};
